import { useMutation, useQueryClient } from 'react-query'
import { PageView, PageTopBar, Notification } from "../../components"
import { useNaturaWarehouseValidation } from "./hooks/useNaturaWarehouseValidation"
import { useCsvProcessing } from "./hooks/useCsvProcessing"
import { WarehouseValidation } from "./components/WarehouseValidation"
import { CsvUploadSection } from "./components/CsvUploadSection"
import { ProcessedDataTable } from "./components/ProcessedDataTable"
import { CsvErrorModal } from "./components/modals/CsvErrorModal"
import { ConfigureProductModal } from "./components/modals/ConfigureProductModal"
import { SuccessPage } from "./components/SuccessPage"
import { FullscreenLoader } from "./components/FullscreenLoader"
import { saveNaturaReplenishment, createNaturaProduct } from "../../services"
import { processApiValidationErrors, formatModalErrors } from "./utils/apiErrorHandler"
import { useState } from 'react'

export const ReplenishmentForm = () => {
  const warehouse = useNaturaWarehouseValidation()
  const csv = useCsvProcessing()
  const queryClient = useQueryClient()
  const [notification, setNotification] = useState({ show: false, title: '', type: 'success' })
  const [showSuccessPage, setShowSuccessPage] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [showConfigureProductModal, setShowConfigureProductModal] = useState(false)
  const [selectedSku, setSelectedSku] = useState('')
  const [selectedCost, setSelectedCost] = useState(0)

  const saveReplenishmentMutation = useMutation(saveNaturaReplenishment, {
    onSuccess: (data) => {
      setSuccessMessage(data.message || 'SKUs validados com sucesso!')
      setShowSuccessPage(true)
      csv.clearData()
    },
    onError: (error) => {
      if (error?.response?.data?.errors) {
        const validationErrors = error.response.data.errors
        const { modalErrors, tableErrors } = processApiValidationErrors(validationErrors)
        
        // Se há erros de modal, não mostrar tabela - apenas o modal
        if (modalErrors.length > 0) {
          const modalErrorMessage = formatModalErrors(modalErrors)
          
          // Limpar todos os dados da tabela
          csv.setCsvData([])
          csv.setCsvErrors([])
          
          // Mostrar modal com erros
          csv.setCsvError(modalErrorMessage)
          csv.setShowErrorModal(true)
        } else if (tableErrors.length > 0) {
          // Processar erros que devem ser mostrados na tabela (apenas se não há erros de modal)
          const updatedErrors = [...csv.csvErrors]
          const updatedData = []

          // Criar mapa de erros de validação por SKU
          const validationErrorMap = new Map()
          tableErrors.forEach(error => {
            validationErrorMap.set(error.sku, error)
          })

          // Processar dados válidos, movendo para erros se necessário
          csv.csvData.forEach(item => {
            const validationResult = validationErrorMap.get(item.sku)
            
            if (validationResult && validationResult.errors.length > 0) {
              // Mover para lista de erros - garantindo que todos os campos sejam preservados
              updatedErrors.push({
                lineNumber: item.lineNumber,
                sku: item.sku,
                quantity: item.quantity,
                cost: item.cost || 0, // Garantir que o custo seja preservado
                error: validationResult.errors.join('; '),
                isProductNotFound: validationResult.isProductNotFound
              })
            } else {
              // Manter na lista de dados válidos
              updatedData.push(item)
            }
          })

          csv.setCsvData(updatedData)
          csv.setCsvErrors(updatedErrors)
          
          // Mostrar notificação para erros de tabela
          setNotification({
            show: true,
            title: `Encontrados erros de validação em ${tableErrors.length} SKU(s). Verifique a tabela para detalhes.`,
            type: 'error'
          })
        }
      } else {
        setNotification({
          show: true,
          title: error.message || 'Erro ao validar SKUs. Tente novamente.',
          type: 'error'
        })
      }
    }
  })

  const createProductMutation = useMutation(
    (productData) => createNaturaProduct(productData, warehouse.warehouse_id),
    {
      onSuccess: (data) => {
        setNotification({
          show: true,
          title: data.message || 'Produto criado com sucesso!',
          type: 'success'
        })

        queryClient.invalidateQueries(['natura-ncm-list'])
        
        // Remove o erro da lista e adiciona o SKU de volta aos dados válidos
        csv.removeErrorBySku(selectedSku)
        
        setShowConfigureProductModal(false)
        setSelectedSku('')
      },
      onError: (error) => {
        setNotification({
          show: true,
          title: error?.response?.data?.message || 'Erro ao criar produto. Tente novamente.',
          type: 'error'
        })
      }
    }
  )

  const handleSave = () => {
    if (!csv.isDataValid || csv.csvData.length === 0) return

    const replenishmentData = {
      warehouse_id: warehouse.warehouse_id,
      // store_id: warehouse.store_id,
      items: csv.csvData.map(item => {
        return {
          sku: item.sku,
          quantity: item.quantity,
          cost: item.cost
        }
      })
    }

    saveReplenishmentMutation.mutate(replenishmentData)
  }

  const handleNewReceiving = () => {
    setShowSuccessPage(false)
    setSuccessMessage('')
    csv.clearData()
    setNotification({ show: false, title: '', type: 'success' })
  }

  const handleConfigureProduct = (sku, cost) => {
    setSelectedSku(sku)
    setSelectedCost(cost)
    setShowConfigureProductModal(true)
  }

  const handleCloseConfigureProductModal = () => {
    setShowConfigureProductModal(false)
    setSelectedSku('')
  }

  const handleConfirmCreateProduct = (productData) => {
    createProductMutation.mutate(productData)
  }

  // Mostrar página de sucesso se salvamento foi bem sucedido
  if (showSuccessPage) {
    return (
      <SuccessPage 
        onNewReceiving={handleNewReceiving}
        successMessage={successMessage}
      />
    )
  }

  if (warehouse.showError) {
    return (
      <WarehouseValidation 
        onNavigateToWarehouses={warehouse.navigateToWarehouses}
      />
    )
  }

  if (!warehouse.allowedWarehouses) {
    return (
      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">Recebimento Natura</div>
          </PageTopBar>
        }
      >
        <div className="text-center py-8">
          <div>Carregando...</div>
        </div>
      </PageView>
    )
  }

  return (
    <>
      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">
              Recebimento Natura
            </div>
          </PageTopBar>
        }
      >
        <div className="max-w-4xl mx-auto">
          <CsvUploadSection 
            onFileUpload={csv.handleFileUpload}
            isProcessing={csv.isProcessing}
            onDownloadTemplate={csv.generateCsvTemplate}
          />

          <ProcessedDataTable 
            csvData={csv.csvData}
            csvErrors={csv.csvErrors}
            onClearData={csv.clearData}
            getTotalQuantity={csv.getTotalQuantity}
            getTotalCost={csv.getTotalCost}
            onSave={handleSave}
            isDataValid={csv.isDataValid}
            isSaving={saveReplenishmentMutation.isLoading}
            onConfigureProduct={handleConfigureProduct}
          />
        </div>

        <CsvErrorModal
          isOpen={csv.showErrorModal}
          onClose={csv.closeErrorModal}
          error={csv.csvError}
        />

        <Notification
          show={notification.show}
          title={notification.title}
          type={notification.type}
          setShow={(show) => setNotification({ ...notification, show })}
        />

        <ConfigureProductModal
          isOpen={showConfigureProductModal}
          onClose={handleCloseConfigureProductModal}
          sku={selectedSku}
          cost={selectedCost}
          onConfirm={handleConfirmCreateProduct}
          isLoading={createProductMutation.isLoading}
        />
      </PageView>

      {/* Loader Fullscreen durante salvamento */}
      <FullscreenLoader show={saveReplenishmentMutation.isLoading} />
    </>
  )
}